import { defineStore } from "pinia";
import { useAuthStore } from "@/stores/authStores";
import api from "@/services/api";
import { useContactsStore } from "@/stores/contactsStore";

export const useSDSStore = defineStore("sds", {
    state: () => ({
        messages: [],
        newMessage: "",
        history: [],
        selectedMessage: [],
        lostSDS: false,
        sdsHistoryActive: false,
    }),

    getters: {
        getLatest() {
            return this.messages.length > 0 ? this.messages[0] : null;
        },
    },

    actions: {
        async acknowledge() {
            const ids = this.selectedMessage.map((msg) => msg.value.id);

            try {
                await api.put(
                    "/sds/acknowledge",
                    {},
                    {
                        headers: { "Content-Type": "application/json" },
                        params: { ids: ids.join(",") },
                    }
                );
                await Promise.all([this.fetchInbox(), this.fetchHistory()]);
            } catch (error) {
                console.error("There was an error adding the message to db:", error);
            } finally {
                this.selectedMessage = [];
            }
        },

        async acknowledgeAll() {
            const ids = this.messages.map((msg) => msg.id);

            try {
                await api.put(
                    "/sds/acknowledge",
                    {},
                    {
                        headers: { "Content-Type": "application/json" },
                        params: { ids: ids.join(",") },
                    }
                );
                await Promise.all([this.fetchInbox(), this.fetchHistory()]);
            } catch (error) {
                console.error("There was an error adding the message to db:", error);
            } finally {
                this.selectedMessage = [];
            }
        },

        // ✅ returns a string reliably
        async searchContact(isdn) {
            const contactsStore = useContactsStore();

            // handheld first
            const hh = contactsStore.handheld.find((h) => h.isdn === isdn);
            if (hh) return hh.radioId;

            // train lookup
            try {
                const response = await api.get("/train/getTrainDetailByIsdn", {
                    params: { user: isdn },
                });

                if (response.data && response.data !== "") {
                    return response.data.trainId;
                }
                return `Unknown (${isdn})`;
            } catch (error) {
                console.error("There was an error fetching data of the caller:", error);
                return `Unknown (${isdn})`;
            }
        },

        // ✅ wait for names, then post
        async onMessageIn(data) {
            const from = data.value.from;
            const to = data.value.to;

            const senderName = await this.searchContact(from);

            const newMessage = {
                id: data.value.msgid,
                sender: from,
                receiver: to,
                senderName,
                receiverName: useAuthStore().loggedUserProfile,
                time: Date.now(),
                message: data.value.content,
                ack: false,
            };

            try {
                await api.post("/sds/newMsg", newMessage, {
                    headers: { "Content-Type": "application/json" },
                });

                await this.fetchInbox();

                if (!this.sdsHistoryActive) {
                    this.lostSDS = true;
                }
            } catch (error) {
                console.error("There was an error adding the message to db:", error);
            }
        },

        // ✅ wait for names, then post
        async onMessageSent(data) {
            const from = data.value.from;
            const to = data.value.to;

            const receiverName = await this.searchContact(to);

            const newOutgoingMessage = {
                id: data.value.msgid,
                sender: from,
                receiver: to,
                senderName: useAuthStore().loggedUserProfile,
                receiverName,
                time: Date.now(),
                message: data.value.content,
                ack: false,
            };

            try {
                await api.post("/sds/newMsg", newOutgoingMessage, {
                    headers: { "Content-Type": "application/json" },
                });

                await this.fetchHistory();
            } catch (error) {
                console.error("There was an error adding the message to db:", error);
            } finally {
                this.newMessage = "";
            }
        },

        async fetchHistory() {
            try {
                const response = await api.get("/sds/getHistory", {
                    params: { user: useAuthStore().loggedUser },
                });
                this.history = response.data;
            } catch (error) {
                console.error("There was an error fetching the call history:", error);
            }
        },

        async fetchInbox() {
            try {
                const response = await api.get("/sds/getInbox", {
                    params: { user: useAuthStore().loggedUser },
                });
                this.messages = response.data;
            } catch (error) {
                console.error("There was an error fetching the call history:", error);
            }
        },
    },
});
